import type { Pixel } from "@pdfking/math"
import type {
  ICCPointer,
  PageNumber,
  PageResult,
  PdfiumAPI,
} from "@pdfking/pdfium"
import type {
  PDFRasterizeFunction,
  PDFRasterizeResult,
  PDFReaderOptions,
} from "@pdfking/renderer-api"
import { ExportImage, UrlLoad } from "@pdfking/compatibility"
import { PSOcoated_v3_small as DefaultICC } from "@pdfking/icc"
import { Provision } from "@pdfking/pdfium"
import { AutoThrow } from "../utils/AutoThrow"
import { OpenReader } from "../utils/OpenReader"
import { PageRange } from "../utils/PageRange"

const loadIccProfile = async (
  api: PdfiumAPI,
  iccUrl?: string
): Promise<ICCPointer> => {
  const iccBytes = await UrlLoad(iccUrl ?? DefaultICC)
    .then(r => r.arrayBuffer())
    .then(b => new Uint8Array(b))
  return AutoThrow(await api.icc_create(iccBytes))
}

const toRasterizeResult = async (
  result: PageResult,
  page: number
): Promise<PDFRasterizeResult> => {
  const width = result.media.size.width as Pixel
  const height = result.media.size.height as Pixel
  const blob = await ExportImage({ data: result.bytes, width, height })
  return { blob, page, width, height }
}

async function* PDFRasterizeImpl(
  api: PdfiumAPI,
  input: PDFReaderOptions,
  image_dpi: Pixel,
  rangeFn?: (page_count: number) => Iterable<number>
): AsyncGenerator<PDFRasterizeResult> {
  const [{ handle, page_count, ensure_page, close }, iccHandle] =
    await Promise.all([
      OpenReader(api, input.stream, input.size),
      loadIccProfile(api, input.icc),
    ])

  try {
    for (const page of PageRange(page_count, rangeFn)) {
      await ensure_page(page - 1)
      yield await toRasterizeResult(
        AutoThrow(
          await api.rasterize_page(
            handle,
            (page - 1) as PageNumber,
            image_dpi,
            iccHandle
          )
        ),
        page
      )
    }
  } finally {
    if (iccHandle) AutoThrow(await api.icc_destroy(iccHandle))
    await close()
  }
}

export const PDFRasterize: PDFRasterizeFunction = (
  input,
  image_dpi = 72 as Pixel,
  rangeFn
) => Provision(api => PDFRasterizeImpl(api, input, image_dpi, rangeFn))
